import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  MapPin, Clock, Users, ChevronLeft, ChevronRight, Sun, BookOpen,
  FlaskConical, Code, Cog, Calculator, Bot, Loader2, ArrowRight, Filter, List, Grid3X3
} from 'lucide-react';
import axios from 'axios';
import { formatTime } from '../../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  science: { label: 'Science', color: 'bg-rose-500', text: 'text-rose-400', light: 'bg-rose-500/15 border-rose-500/30 text-rose-300', icon: FlaskConical },
  coding: { label: 'Technology', color: 'bg-blue-500', text: 'text-blue-400', light: 'bg-blue-500/15 border-blue-500/30 text-blue-300', icon: Code },
  engineering: { label: 'Engineering', color: 'bg-emerald-500', text: 'text-emerald-400', light: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300', icon: Cog },
  math: { label: 'Math', color: 'bg-purple-500', text: 'text-purple-400', light: 'bg-purple-500/15 border-purple-500/30 text-purple-300', icon: Calculator },
  robotics: { label: 'Robotics', color: 'bg-orange-500', text: 'text-orange-400', light: 'bg-orange-500/15 border-orange-500/30 text-orange-300', icon: Bot },
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('camps'); // 'camps' | 'courses'
  const [viewMode, setViewMode] = useState('list');
  const [currentMonth, setCurrentMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [locationFilter, setLocationFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0);
    const params = new URLSearchParams({
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
    });
    if (locationFilter !== 'all') params.set('location', locationFilter);
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    if (gradeFilter !== 'all') params.set('grade_band', gradeFilter);

    axios.get(`${API_URL}/api/calendar/?${params}`)
      .then(res => {
        setEvents(res.data.events || []);
        if (res.data.locations) setLocations(res.data.locations);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentMonth, locationFilter, categoryFilter, gradeFilter]);

  // Split events by type
  const campEvents = useMemo(() => events.filter(e => e.type === 'camp'), [events]);
  const courseEvents = useMemo(() => events.filter(e => e.type === 'course'), [events]);
  const activeEvents = activeTab === 'camps' ? campEvents : courseEvents;

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = {};
    activeEvents.forEach(e => {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    return map;
  }, [activeEvents]);

  // For camps: group by week (start_date of the camp section)
  const campsByWeek = useMemo(() => {
    if (activeTab !== 'camps') return [];
    // Group camp events by camp_id + location to get unique camp-week-location combos
    const weekMap = {};
    campEvents.forEach(e => {
      const key = `${e.camp_id}-${e.location_id}`;
      if (!weekMap[key]) {
        weekMap[key] = {
          camp_name: e.camp_name,
          camp_id: e.camp_id,
          location_name: e.location_name,
          location_city: e.location_city,
          location_eligibility: e.location_eligibility,
          location_eligibility_detail: e.location_eligibility_detail,
          external_purchase: e.external_purchase,
          external_purchase_url: e.external_purchase_url,
          grade_bands: e.grade_bands,
          price: e.price,
          spots_remaining: e.spots_remaining,
          start_time: e.start_time,
          end_time: e.end_time,
          days: [],
        };
      }
      weekMap[key].days.push(e);
    });
    // Sort by first day date
    return Object.values(weekMap)
      .map(w => ({ ...w, start_date: w.days[0]?.date, days: w.days.sort((a, b) => a.date.localeCompare(b.date)) }))
      .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
  }, [campEvents, activeTab]);

  // Calendar grid
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ day: d, date: dateStr, events: eventsByDate[dateStr] || [] });
    }
    return days;
  }, [currentMonth, eventsByDate]);

  const eventDates = useMemo(() => [...new Set(activeEvents.map(e => e.date))].sort(), [activeEvents]);

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  return (
    <div data-testid="calendar-page">
      {/* Hero */}
      <section className="relative py-8 bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847]">
        <div className="container-app">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-1">Course Calendar</h1>
              <p className="text-sm text-white/50">See what's offered, where, and when</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setViewMode('calendar')} className={`p-2 rounded-lg transition-colors ${viewMode === 'calendar' ? 'bg-rsc-yellow text-rsc-navy' : 'bg-white/10 text-white/50 hover:text-white'}`}>
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-rsc-yellow text-rsc-navy' : 'bg-white/10 text-white/50 hover:text-white'}`}>
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-6 mb-4">
            <button onClick={() => setActiveTab('camps')}
              className={`flex items-center gap-2 pb-2 text-sm font-bold border-b-2 transition-all ${activeTab === 'camps' ? 'text-rsc-yellow border-rsc-yellow' : 'text-white/40 border-transparent hover:text-white/60'}`}>
              <Sun className="w-4 h-4" /> Summer Camps
              <Badge className={`text-[10px] ${activeTab === 'camps' ? 'bg-rsc-yellow/20 text-rsc-yellow' : 'bg-white/10 text-white/40'}`}>{campEvents.length}</Badge>
            </button>
            <button onClick={() => setActiveTab('courses')}
              className={`flex items-center gap-2 pb-2 text-sm font-bold border-b-2 transition-all ${activeTab === 'courses' ? 'text-rsc-yellow border-rsc-yellow' : 'text-white/40 border-transparent hover:text-white/60'}`}>
              <BookOpen className="w-4 h-4" /> Afterschool Courses
              <Badge className={`text-[10px] ${activeTab === 'courses' ? 'bg-rsc-yellow/20 text-rsc-yellow' : 'bg-white/10 text-white/40'}`}>{courseEvents.length}</Badge>
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[220px] bg-white/10 border-white/15 text-white text-sm h-9">
                <MapPin className="w-3.5 h-3.5 mr-1.5 text-white/40" /><SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px] bg-white/10 border-white/15 text-white text-sm h-9">
                <Filter className="w-3.5 h-3.5 mr-1.5 text-white/40" /><SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="science">Science</SelectItem>
                <SelectItem value="coding">Technology</SelectItem>
                <SelectItem value="engineering">Engineering</SelectItem>
                <SelectItem value="math">Math</SelectItem>
                <SelectItem value="robotics">Robotics</SelectItem>
              </SelectContent>
            </Select>
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="w-[140px] bg-white/10 border-white/15 text-white text-sm h-9">
                <Users className="w-3.5 h-3.5 mr-1.5 text-white/40" /><SelectValue placeholder="All Grades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                <SelectItem value="K-2">K–2</SelectItem>
                <SelectItem value="3-5">Grades 3–5</SelectItem>
                <SelectItem value="6-8">Grades 6–8</SelectItem>
              </SelectContent>
            </Select>
            <Badge className="bg-white/10 text-white/60 border-white/15 text-xs self-center">{activeEvents.length} events</Badge>
          </div>
        </div>
      </section>

      <section className="py-8 bg-gray-50 min-h-[60vh]">
        <div className="container-app">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="outline" size="sm" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
            <h2 className="text-xl font-bold text-rsc-navy">{MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h2>
            <Button variant="outline" size="sm" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
          </div>

          {loading && <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-rsc-blue mx-auto" /></div>}

          {/* ── CALENDAR GRID VIEW ── */}
          {!loading && viewMode === 'calendar' && (
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="grid grid-cols-7">
                {DAY_LABELS.map(d => (
                  <div key={d} className="p-2 text-center text-xs font-bold text-gray-500 border-b bg-gray-50">{d}</div>
                ))}
                {calendarDays.map((cell, i) => (
                  <div key={i} className={`min-h-[100px] border-b border-r p-1 ${cell ? 'bg-white' : 'bg-gray-50/50'}`}>
                    {cell && (
                      <>
                        <div className="text-xs font-semibold text-gray-400 mb-1">{cell.day}</div>
                        <div className="space-y-0.5">
                          {cell.events.slice(0, 3).map(ev => {
                            const cfg = CATEGORY_CONFIG[ev.category] || CATEGORY_CONFIG.science;
                            return (
                              <div key={ev.id} className={`text-[9px] px-1 py-0.5 rounded ${cfg.light} border truncate`} title={`${ev.title} @ ${ev.location_name}`}>
                                {ev.title}
                              </div>
                            );
                          })}
                          {cell.events.length > 3 && <div className="text-[9px] text-gray-400 px-1">+{cell.events.length - 3} more</div>}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── LIST VIEW: SUMMER CAMPS (grouped by week + location) ── */}
          {!loading && viewMode === 'list' && activeTab === 'camps' && (
            <div className="space-y-4">
              {campsByWeek.length === 0 && (
                <div className="text-center py-12 text-gray-500">No summer camp sessions found for this date range and filters.</div>
              )}
              {campsByWeek.map((week, idx) => {
                const isRestricted = week.location_eligibility !== 'open';
                const firstDate = week.days[0] ? new Date(week.days[0].date + 'T00:00:00') : null;
                const lastDate = week.days[week.days.length - 1] ? new Date(week.days[week.days.length - 1].date + 'T00:00:00') : null;
                return (
                  <div key={idx} className="rounded-lg border bg-white p-4">
                    {/* Week header */}
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-rsc-navy flex items-center justify-center flex-shrink-0">
                          <Sun className="w-5 h-5 text-rsc-yellow" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-rsc-navy">
                            {firstDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            {lastDate && ` – ${lastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                            <span className="font-normal text-gray-500 ml-2">· {week.days.length} days</span>
                          </h3>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {week.location_name}</span>
                            {week.start_time && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {formatTime(week.start_time)}–{formatTime(week.end_time)}</span>}
                            {week.grade_bands?.map(g => <Badge key={g} variant="outline" className="text-[10px]">{g}</Badge>)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {isRestricted ? (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">{week.location_eligibility_detail || 'Restricted'}</Badge>
                        ) : (
                          <Badge className="bg-green-50 text-green-600 border-green-200 text-[10px]">Open to All</Badge>
                        )}
                        <div className="text-right">
                          <p className="text-lg font-bold text-rsc-navy">${week.price}</p>
                          <p className="text-[10px] text-gray-400">{week.spots_remaining} spots</p>
                        </div>
                        {week.external_purchase ? (
                          <a href={week.external_purchase_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="text-xs h-8">Register ↗</Button>
                          </a>
                        ) : (
                          <Link to="/summer-camps">
                            <Button size="sm" className="text-xs h-8 bg-rsc-yellow text-rsc-navy font-bold">Register</Button>
                          </Link>
                        )}
                      </div>
                    </div>
                    {/* Daily schedule strip */}
                    <div className="flex gap-1.5">
                      {week.days.map(day => {
                        const cfg = CATEGORY_CONFIG[day.category] || CATEGORY_CONFIG.science;
                        const CatIcon = cfg.icon;
                        const dayDate = new Date(day.date + 'T00:00:00');
                        return (
                          <div key={day.id} className="flex-1 rounded-md bg-gray-50 border p-2 text-center min-w-0">
                            <div className="text-[9px] font-bold text-gray-400 uppercase">{dayDate.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                            <div className={`w-7 h-7 rounded-md ${cfg.color} flex items-center justify-center mx-auto my-1`}>
                              <CatIcon className="w-3.5 h-3.5 text-white" />
                            </div>
                            <p className="text-[9px] font-semibold text-gray-700 truncate">{day.title}</p>
                            <p className="text-[8px] text-gray-400">{cfg.label}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── LIST VIEW: AFTERSCHOOL COURSES (grouped by date → location) ── */}
          {!loading && viewMode === 'list' && activeTab === 'courses' && (
            <div className="space-y-6">
              {eventDates.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="font-semibold text-gray-600 mb-1">No afterschool courses scheduled yet</p>
                  <p className="text-sm text-gray-400">Afterschool courses for Fall 2026 will be posted soon. Check back or browse our summer camps!</p>
                  <Link to="/summer-camps" className="mt-4 inline-block">
                    <Button className="bg-rsc-yellow text-rsc-navy font-bold rounded-full text-sm">Browse Summer Camps <ArrowRight className="w-4 h-4 ml-1" /></Button>
                  </Link>
                </div>
              )}
              {eventDates.map(date => {
                const dateObj = new Date(date + 'T00:00:00');
                const dayEvents = eventsByDate[date] || [];
                const byLocation = {};
                dayEvents.forEach(e => {
                  const key = e.location_name;
                  if (!byLocation[key]) byLocation[key] = { location: e, events: [] };
                  byLocation[key].events.push(e);
                });
                return (
                  <div key={date}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-rsc-navy flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-[10px] text-white/50 uppercase leading-none">{dateObj.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                        <span className="text-lg font-extrabold text-white leading-none">{dateObj.getDate()}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-rsc-navy">{dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
                        <p className="text-xs text-gray-500">{dayEvents.length} session{dayEvents.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="space-y-2 ml-[60px]">
                      {Object.entries(byLocation).map(([locName, { location: loc, events: locEvents }]) => (
                        <div key={locName} className="rounded-lg border bg-white p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <MapPin className="w-3.5 h-3.5 text-rsc-blue" />
                            <span className="text-sm font-semibold text-rsc-navy">{locName}</span>
                            {loc.location_eligibility !== 'open' ? (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] ml-auto">{loc.location_eligibility_detail || 'Restricted'}</Badge>
                            ) : (
                              <Badge className="bg-green-50 text-green-600 border-green-200 text-[10px] ml-auto">Open to All</Badge>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            {locEvents.map(ev => {
                              const cfg = CATEGORY_CONFIG[ev.category] || CATEGORY_CONFIG.science;
                              const CatIcon = cfg.icon;
                              return (
                                <div key={ev.id} className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-gray-50">
                                  <div className={`w-8 h-8 rounded-md ${cfg.color} flex items-center justify-center flex-shrink-0`}>
                                    <CatIcon className="w-4 h-4 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-semibold text-gray-900 truncate">{ev.title}</span>
                                    <div className="flex items-center gap-3 text-xs text-gray-500">
                                      {ev.start_time && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {formatTime(ev.start_time)}–{formatTime(ev.end_time)}</span>}
                                      <span>{ev.grade_bands?.join(', ')}</span>
                                      <span>{ev.spots_remaining} spots</span>
                                    </div>
                                  </div>
                                  {ev.course_id && (
                                    <Link to={`/courses/${ev.course_id}`}>
                                      <Button size="sm" variant="outline" className="text-xs h-7">Details</Button>
                                    </Link>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
